import { useState } from 'react';
import Lightbox from 'yet-another-react-lightbox';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/thumbnails.css';
import { FileText } from '@phosphor-icons/react';
import type { ChatMessageAttachment } from '@/slices/chat/chatSlice';
import { useChatDocumentViewer } from '@/hooks/useChatDocumentViewer';

const ChatAttachedImages = ({
  attachments,
  onReload,
}: {
  attachments: ChatMessageAttachment[];
  /** Re-reads the conversation to replace an expired presigned URL. */
  onReload?: () => void;
}) => {
  const { open: openSideViewer } = useChatDocumentViewer();
  const [index, setIndex] = useState(-1);
  const [expiredUrls, setExpiredUrls] = useState<Set<string>>(() => new Set());

  // An attached document opens beside the chat (ORA-736).
  const handleDocumentOpen = (documentId: string) =>
    openSideViewer({ kind: 'chatDocument', documentId });

  const documents = attachments.filter(
    (item) => item.kind === 'document' && item.documentId
  );

  const slides = attachments
    .filter((item) => item.kind !== 'document')
    .flatMap((item) => (item.previewUrl ? [{ src: item.previewUrl }] : []));

  if (!slides.length && !documents.length) return null;
  return (
    <>
      {documents.length ? (
        <div className="mb-2 space-y-1" aria-label="Attached documents">
          {documents.map((document) => (
            <button
              key={document.documentId}
              type="button"
              onClick={() => handleDocumentOpen(document.documentId!)}
              className="flex w-full items-center gap-2 rounded-lg border border-border-subtle bg-surface-muted px-2 py-1.5 text-left text-xs text-fg-primary hover:bg-surface-highlight"
            >
              <FileText size={16} aria-hidden="true" />
              <span className="min-w-0 flex-1 truncate">
                {document.fileName ?? 'Document'}
              </span>
              <span className="text-fg-secondary">
                {document.pageCount ? `${document.pageCount} pages` : 'Open'}
              </span>
            </button>
          ))}
        </div>
      ) : null}
      {slides.length ? (
        <div
          role="group"
          aria-label="Message attachments"
          className={`mb-2 grid gap-2 ${slides.length === 1 ? 'max-w-[240px] [@media(max-width:560px)]:max-w-[200px]' : 'w-[240px] max-w-full grid-cols-2'}`}
        >
          {slides.map((slide, i) =>
            expiredUrls.has(slide.src) ? (
              <div
                key={`${slide.src}-${i}`}
                className="flex aspect-square flex-col items-center justify-center rounded-lg border border-border-subtle p-2 text-center text-xs text-fg-secondary"
              >
                <span>Image expired</span>
                <button
                  type="button"
                  onClick={onReload}
                  disabled={!onReload}
                  className="mt-1 underline focus-visible:outline focus-visible:outline-2"
                >
                  Reload
                </button>
              </div>
            ) : (
              <button
                key={`${slide.src}-${i}`}
                type="button"
                aria-label={`Open attached image ${i + 1}`}
                onClick={() => setIndex(i)}
                className="overflow-hidden rounded-lg focus-visible:outline focus-visible:outline-2"
              >
                <img
                  src={slide.src}
                  alt={`Attached image ${i + 1}`}
                  onError={() =>
                    setExpiredUrls((current) => new Set(current).add(slide.src))
                  }
                  className={
                    slides.length === 1
                      ? 'h-auto w-full'
                      : 'aspect-square w-full object-cover'
                  }
                />
              </button>
            )
          )}
        </div>
      ) : null}
      <Lightbox
        open={index >= 0}
        close={() => setIndex(-1)}
        index={Math.max(index, 0)}
        slides={slides}
        plugins={slides.length > 1 ? [Thumbnails, Zoom] : [Zoom]}
        carousel={{ finite: true }}
      />
    </>
  );
};

export default ChatAttachedImages;
